import axios from 'axios';
import api from '../services/api';
import { ICompany } from '../shared/interfaces';
import { IDocument } from '../shared/interfaces/document.interface';

const BACKEND_URL =
  (process.env.REACT_APP_BACKEND_URL || '').trim() || 'http://localhost:3334';

// Instância de API pública para o portal do paciente (sem autenticação de clínica padrão)
const patientApi = axios.create({
  baseURL: BACKEND_URL,
});

// Injeta tenant e token de paciente antes de cada requisição
patientApi.interceptors.request.use((config) => {
  const tenant = sessionStorage.getItem('@Sigeclin:patientTenant');
  const token = sessionStorage.getItem('@Sigeclin:patientToken');
  if (tenant) config.headers['x-tenant-id'] = tenant;
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

export interface IPatientPortalLoginPayload {
  cpf_cnpj: string;
  birthday: string; // YYYY-MM-DD
}

export interface IPatientProfile {
  id: string;
  code: number;
  corporate_name: string;
  fantasy_name: string | null;
  cpf_cnpj: string;
  birthday: string;
  cell_phone: string | null;
  email: string | null;
}

export interface IPatientSchedule {
  id: string;
  title: string;
  start: string;
  end: string;
  observation: string | null;
  confirmation_status: string;
  professional: { id: string; name: string } | null;
}

export interface IPatientFinancial {
  id: string;
  code: number;
  description: string;
  value: number;
  discount: number;
  taxe: number;
  issue_date: string;
  due_date: string;
  payment_date: string | null;
  status: number; // 1-Aberto, 2-Pago, 5-Cancelado
}

export interface IPatientHealthCertificate {
  id: string;
  code?: number;
  client_id?: string;
  professional_id?: string;
  health_certificate_id?: string;
  start_time?: string;
  end_time?: string;
  title?: string;
  title_health_certificate?: string;
  text_description: string;
  date: string;
  quantity_days: number;
  cid: string | null;
  client_name?: string;
  professional_name?: string;
  terminology_code?: string;
  terminology_description?: string;
  cpf?: string;
  rqe?: string;
  signature_validation_code?: string;
  signed_at?: string | null;
  professional_signature?: string | null;
  created_at: string;
}

export interface IPatientDocument {
  id: string;
  code?: number;
  title?: string;
  kind?: 'GENERAL' | 'PRESCRIPTION';
  text_description: string;
  prescription_items?: string;
  prescription_guidelines?: string | null;
  prescription_date?: string | null;
  signature_validation_code?: string | null;
  signed_at?: string | null;
  created_at: string;
}

export interface IPatientFormField {
  input_label: string;
  input_type?: string;
  value: string;
}

export interface IPatientFormEvolution {
  id: string;
  date: string;
  time: string;
  observation: string | null;
  rank: number;
  professional_name?: string | null;
  signature_validation_code?: string | null;
  signed_at?: string | null;
}

export interface IPatientForm {
  id: string;
  code: number;
  title: string;
  description?: string | null;
  professional_name?: string | null;
  created_at: string;
  signature_validation_code?: string | null;
  signed_at?: string | null;
  fields: IPatientFormField[];
  evolutions: IPatientFormEvolution[];
}

export interface IPatientTreatmentItem {
  id: string;
  status: number; // 0-pendente, 1-andamento, 2-finalizado
  quantity: number;
  value: number;
  discount: number;
  procedure: { id: string; description: string } | null;
}

export interface IPatientTreatmentLog {
  id: string;
  treatment_id: string;
  date: string;
  time: string;
  status: number;
  rank: number;
  observation: string | null;
  procedure_description: string | null;
  professional_name: string | null;
  signature_validation_code: string | null;
  signed_at: string | null;
}

export interface IPatientTreatment {
  id: string;
  code: number;
  type: number; // 0-orçamento, 1-tratamento
  total: number;
  discount: number;
  progress: number;
  approval: boolean | null;
  created_at: string;
  professional: { id: string; name: string } | null;
  items: IPatientTreatmentItem[];
  logs: IPatientTreatmentLog[];
}

export const patientLogin = async (
  tenant: string,
  payload: IPatientPortalLoginPayload,
): Promise<{ client: IPatientProfile; access_token: string }> => {
  sessionStorage.setItem('@Sigeclin:patientTenant', tenant);
  sessionStorage.setItem('@Sigeclin:clinic', tenant);
  api.defaults.headers.common['x-tenant-id'] = tenant;
  const response = await patientApi.post('/clients/portal/login', payload);
  return response.data;
};

export const patientGetProfile = async (): Promise<IPatientProfile> => {
  const response = await patientApi.get('/clients/portal/me');
  return response.data;
};

export const patientGetSchedules = async (): Promise<IPatientSchedule[]> => {
  const response = await patientApi.get('/clients/portal/schedules');
  return response.data;
};

export const patientGetFinancials = async (): Promise<IPatientFinancial[]> => {
  const response = await patientApi.get('/clients/portal/financials');
  return response.data;
};

export const patientGetHealthCertificates = async (): Promise<
  IPatientHealthCertificate[]
> => {
  const response = await patientApi.get('/clients/portal/health-certificates');
  return response.data;
};

export const patientGetHealthCertificateById = async (
  id: string,
): Promise<IPatientHealthCertificate> => {
  const response = await patientApi.get(
    `/clients/portal/health-certificates/${id}`,
  );
  return response.data;
};

export const patientGetCompany = async (): Promise<ICompany | null> => {
  const response = await patientApi.get('/clients/portal/company');
  return response.data;
};

export const patientGetDocuments = async (): Promise<IPatientDocument[]> => {
  const response = await patientApi.get('/clients/portal/documents');
  return response.data;
};

export const patientGetPrescriptions = async (): Promise<
  IPatientDocument[]
> => {
  const response = await patientApi.get('/clients/portal/prescriptions');
  return response.data;
};

export const patientGetForms = async (): Promise<IPatientForm[]> => {
  const response = await patientApi.get('/clients/portal/forms');
  return response.data;
};

export const patientGetDocumentById = async (
  id: string,
): Promise<IDocument> => {
  const response = await patientApi.get(`/clients/portal/documents/${id}`);
  return response.data;
};

export const patientGetTreatments = async (): Promise<IPatientTreatment[]> => {
  const response = await patientApi.get('/clients/portal/treatments');
  return response.data;
};
